<template>
  <div>
    <v-dialog
      v-model="dialog"
      max-width="800px"
      content-class="incident-dialog"
      scrollable
    >
      <v-card>
        <v-card-title>
          <v-row>
            <v-col>
              <span>{{ title }}</span>
            </v-col>
            <v-col cols="1">
              <v-icon right v-on:click="close(false)">mdi-close</v-icon>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text>
          <v-container class="pt-0">
            <v-form ref="form" v-model="validForm">
              <v-row>
                <v-col cols="12" class="py-0">
                  <v-text-field
                    v-model="incident.title"
                    label="Title"
                    :rules="[(v) => !!v || 'Title is required']"
                    required
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="pt-0">
                  <v-textarea
                    dense
                    v-model="incident.description"
                    label="Description"
                    :rules="[(v) => !!v || 'Description is required']"
                    required
                  ></v-textarea>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="4" class="py-0">
                  <v-select
                    dense
                    v-model="incident.category"
                    :items="categories"
                    label="Category"
                    :rules="[(v) => !!v || 'Category is required']"
                    required
                  ></v-select>
                </v-col>
                <v-col cols="2" class="py-0">
                  <v-select
                    dense
                    v-if="profile.severityEnabled"
                    v-model="incident.severity"
                    :items="severityScores"
                    item-text="title"
                    item-value="title"
                    label="Severity"
                    :rules="[(v) => !!v || 'Severity is required']"
                    required
                  ></v-select>
                </v-col>
                <v-col cols="3" class="py-0">
                  <v-menu
                    ref="dateMenu"
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    :return-value.sync="incident.incidentDate"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        dense
                        :value="incidentDateFormatted"
                        label="Incident Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :rules="[(v) => !!v || 'Incident Date is required']"
                        required
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="incident.incidentDate"
                      no-title
                      scrollable
                      :show-current="utcDate"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="dateMenu = false">
                        Cancel
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.dateMenu.save(incident.incidentDate)"
                      >
                        OK
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="3" class="py-0">
                  <v-menu
                    v-if="profile.incidentTimeEnabled"
                    ref="timeMenu"
                    v-model="timeMenu"
                    :close-on-content-click="false"
                    :return-value.sync="incident.incidentTime"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        dense
                        :value="incident.incidentTime"
                        label="Incident Time"
                        prepend-icon="mdi-clock-time-four-outline"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :rules="[(v) => !!v || 'Incident Time is required']"
                        required
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-model="incident.incidentTime"
                      full-width
                      @click:minute="$refs.timeMenu.save(incident.incidentTime)"
                    ></v-time-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="2" class="pt-1">
                  <v-text-field
                    dense
                    v-model="incident.location.latitude"
                    label="Latitude"
                    :rules="[(v) => !!v || 'Latitude is required']"
                    required
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="2" class="pt-1">
                  <v-text-field
                    dense
                    v-model="incident.location.longitude"
                    label="Longitude"
                    :rules="[(v) => !!v || 'Longitude is required']"
                    required
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="5" class="pt-1">
                  <v-select
                    dense
                    v-model="incident.countryIso2Code"
                    :items="countries"
                    item-text="name"
                    item-value="iso"
                    label="Country"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="pt-0">
                  <AssetsEditor
                    :asset="incident.location"
                    @change="handleLocationChanged"
                  ></AssetsEditor>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  class="pt-0"
                  v-for="(field, index) in customFields"
                  v-bind:key="index"
                >
                  <v-text-field
                    dense
                    :label="field"
                    v-model="incident.customFields[field]"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text class="default btn mr-5" @click="close(false)">
            Cancel
          </v-btn>
          <v-btn
            class="primary btn mr-3"
            @click="saveIncident"
            :disabled="!validForm || saving"
          >
            Save
            <v-progress-circular
              v-if="saving"
              indeterminate
              size="14"
              width="2"
              class="ml-2"
            ></v-progress-circular>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { MODULE_PREFIX_MY_INCIDENTS, MODULE_PREFIX_TIME } from "@/store";
import { mapGetters } from "vuex";
import { getFormattedDate } from "@/common/date.utilities";
import {
  getAllCountries,
  getCountryFromGooglePlace,
} from "@/common/maps/utilities";
import { SAVE_MY_INCIDENT } from "@/store/mutations.type";
import dayjs from "dayjs";

const EMITTED_ACTIONS = {
  CLOSE: "close",
};

export default {
  name: "MyIncidentDialog",
  data() {
    return {
      countries: [],
      dateMenu: false,
      dialog: false,
      incident: {
        category: "",
        countryIso2Code: undefined,
        incidentDate: undefined,
        incidentTime: undefined,
        description: "",
        location: {
          latitude: 0,
          longitude: 0,
        },
        title: "",
        severity: undefined,
        customFields: {},
      },
      saving: false,
      severityScores: [...Array(11).keys()].slice(1),
      timeMenu: false,
      title: "New Incident",
      validForm: false,
    };
  },
  computed: {
    ...mapGetters({
      utcDate: MODULE_PREFIX_TIME + "today",
      profile: MODULE_PREFIX_MY_INCIDENTS + "profile",
    }),
    incidentDateFormatted() {
      if (this.incident.incidentDate) {
        return getFormattedDate(this.incident.incidentDate);
      }
      return null;
    },
    customFields() {
      return Object.entries(this.profile?.customFields ?? {})
        .filter(([_, enabled]) => enabled)
        .map(([key, _]) => key);
    },
    categories() {
      return (this.profile?.incidentTypes ?? [])
        .filter(({ enabled }) => enabled)
        .map(({ title }) => title)
        .sort();
    },
  },
  components: {
    AssetsEditor: () => import("@/components/assetManagement/AssetsEditor"),
  },
  methods: {
    close(refresh) {
      this.reset();
      this.dialog = false;
      this.$emit(EMITTED_ACTIONS.CLOSE, {
        show: false,
        refresh: refresh ?? false,
      });
    },
    getLocationFromPlace(googlePlace) {
      if (googlePlace.provided_location) {
        return googlePlace.provided_location;
      } else if (googlePlace.geometry?.location) {
        return {
          lat: googlePlace.geometry?.location?.lat(),
          lng: googlePlace.geometry?.location?.lng(),
        };
      }
    },
    handleLocationChanged(newLocation) {
      this.incident.countryIso2Code = getCountryFromGooglePlace(
        newLocation
      ).code;
      const { lng, lat } = this.getLocationFromPlace(newLocation);
      this.incident.location.latitude = lat?.toFixed(5);
      this.incident.location.longitude = lng?.toFixed(5);
      this.$refs.form.validate();
    },
    async loadCountries() {
      const countries = await getAllCountries();
      return countries.map((c) => {
        return {
          name: c.properties.displayName,
          iso: c.properties.iso2,
        };
      });
    },
    reset() {
      this.incident = {
        category: "",
        countryIso2Code: undefined,
        incidentDate: undefined,
        incidentTime: undefined,
        description: "",
        location: {
          latitude: 0,
          longitude: 0,
        },
        severity: undefined,
        title: "",
        customFields: {},
      };
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },
    async saveIncident() {
      if (this.$refs.form.validate()) {
        this.saving = true;
        await this.$store.dispatch(
          MODULE_PREFIX_MY_INCIDENTS + SAVE_MY_INCIDENT,
          this.incident
        );
        this.saving = false;
        this.close(true);
      } else {
        console.error("My incidents form is not valid");
      }
    },
    formatIncidentTime(item) {
      return dayjs(`${item.incidentDate}T${item.incidentTime}`).format("HH:mm");
    },
  },
  async mounted() {
    this.countries = await this.loadCountries();
  },
  props: {
    show: {
      type: Object,
      default: () => {
        return {
          show: false,
          incident: undefined,
        };
      },
    },
  },
  watch: {
    show: {
      handler(event) {
        if (!event.incident) {
          this.title = "New Incident";
          this.reset();
        } else {
          this.title = "Edit Incident";
          this.incident = event.incident;
          this.incident.incidentTime = this.formatIncidentTime(this.incident);
        }
        this.dialog = event.show;
      },
    },
  },
};
</script>
